import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import {
  IMessageBubble,
  messageBubbleStyles,
  requiredMessageBubbleProperties,
} from "../../../models/interactions/MessageBubble";
import { UpdatePropertyOfInteraction } from "../../../app/slices/helpers/TrainingSliceHelper";
import Interaction from "../../../models/interactions/Interaction";
import NodeActorPropDropdown from "../components/NodeActorPropDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import SequenceLocalizedVariableInputField from "../components/SequenceLocalizedVariableInputField";
import NodeNumberField from "../components/NodeNumberField";
import { InteractionProperty } from "../../../models/InteractionProperty";
import MessageBubbleAnimationDropdown from "../../previews/message_bubble/MessageBubbleAnimationDropdown";
import LookatTargetDropdown from "./LookatTargetDropdown";
import usePropHelper from "../../../hooks/PropHelper";
import NodeStringDropdown from "../components/NodeStringDropdown";

export default function MessageBubbleNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const messageBubble = props.interaction as IMessageBubble;
  const { updateInteraction, sequence } = useUpdateSequenceInteraction();
  const take = useSelector(selectSelectedTake3D);
  const { getBubbleTargetProps } = usePropHelper();
  const propsInTake = useMemo(
    () =>
      take
        ? getBubbleTargetProps(take, {
            kind: "sequence",
            nodeId: props.nodeId,
            sequence,
          })
        : [],
    [getBubbleTargetProps, props.nodeId, sequence, take]
  );
  const isPropIdInTake = (objectId: string) =>
    propsInTake.some((prop) => prop.id === objectId);
  const isPropAnchor = isPropIdInTake(messageBubble.npcId);

  useEffect(() => {
    if (!isPropAnchor || !messageBubble.lookAt) return;
    updateInteraction(props.nodeId, {
      ...messageBubble,
      lookAt: "",
    } as IMessageBubble);
  }, [isPropAnchor, messageBubble, props.nodeId, updateInteraction]);

  const actorDropdown = useMemo(
    () => (
      <NodeActorPropDropdown
        label="Actor"
        selectedObjectId={messageBubble.npcId}
        availableProps={propsInTake}
        onClick={(objectId: string) => {
          updateInteraction(props.nodeId, {
            ...messageBubble,
            npcId: objectId,
            lookAt: isPropIdInTake(objectId) ? "" : messageBubble.lookAt,
          } as IMessageBubble);
        }}
      />
    ),
    [messageBubble, propsInTake, updateInteraction, props.nodeId]
  );

  const lookAtDropdown = useMemo(
    () => (
      <LookatTargetDropdown
        label="Look at"
        nodeId={props.nodeId}
        selectedTargetId={messageBubble.lookAt}
        allowNoTarget
        noTargetLabel="No actor"
        disabled={isPropAnchor}
        onClick={(targetId: string) =>
          updateInteraction(props.nodeId, {
            ...messageBubble,
            lookAt: targetId,
          } as IMessageBubble)
        }
      />
    ),
    [isPropAnchor, messageBubble, updateInteraction, props.nodeId]
  );

  const animationDropdown = useMemo(
    () => (
      <MessageBubbleAnimationDropdown
        selectedAnimation={messageBubble.animation}
        variant="node"
        onClick={(animation) =>
          updateInteraction(props.nodeId, {
            ...messageBubble,
            animation: animation,
          } as IMessageBubble)
        }
      />
    ),
    [messageBubble.animation, updateInteraction, props.nodeId]
  );

  const messageStyleDropdown = useMemo(
    () => (
      <NodeStringDropdown
        label="Message Style"
        items={messageBubbleStyles}
        selectedItem={messageBubble.messageBubbleStyle}
        onClick={(messageBubbleStyle: string) =>
          updateInteraction(props.nodeId, {
            ...messageBubble,
            messageBubbleStyle,
          } as IMessageBubble)
        }
      />
    ),
    [messageBubble, props.nodeId, updateInteraction]
  );

  const displayTimeProperty = useMemo(() => {
    return messageBubble.properties?.find((property) => property.id === "displayTime");
  }, [messageBubble.properties]);

  const displayTimeInput = useMemo(
    () => (
      <NodeNumberField
        label="Display Time"
        value={(displayTimeProperty?.value as number) ?? 0}
        onChange={(displayTime) => {
          const baseProperty = displayTimeProperty ?? requiredMessageBubbleProperties.find((property) => property.id === "displayTime");
          if (!baseProperty) return;

          const updatedProperty: InteractionProperty = {
            ...baseProperty,
            value: displayTime,
          };

          const updatedInteraction = displayTimeProperty
            ? UpdatePropertyOfInteraction(messageBubble, updatedProperty)
            : ({
                ...messageBubble,
                properties: [...(messageBubble.properties ?? []), updatedProperty],
              } as IMessageBubble);

          updateInteraction(props.nodeId, updatedInteraction);
        }}
      />
    ),
    [displayTimeProperty, messageBubble, props.nodeId, updateInteraction]
  );

  const messageInput = useMemo(
    () => (
      <SequenceLocalizedVariableInputField
        label="Message"
        placeholder="Message"
        localizationKey={messageBubble.text}
      />
    ),
    [messageBubble.text]
  );

  return (
    <div className="w-full h-full flex flex-col gap-[4px]">
      {actorDropdown}
      {lookAtDropdown}
      {animationDropdown}
      {messageStyleDropdown}
      {displayTimeInput}
      {messageInput}
    </div>
  );
}

